import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useState } from "react";
import { useVenue } from "../shell/VenueContext";
import { useDemoContext, useDemoReset } from "./queries";

const banner = css({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "3",
  padding: "2 3",
  fontSize: "sm",
  color: "#fef3c7",
  backgroundColor: "#7c2d12",
  borderBottomWidth: "1px",
  borderColor: "#b45309",
});
const tag = css({
  fontWeight: "bold",
  letterSpacing: "0.08em",
  borderWidth: "1px",
  borderColor: "#fbbf24",
  borderRadius: "sm",
  padding: "0 2",
});
const note = css({ flex: "1", minWidth: "12rem" });
const btn = css({
  fontSize: "sm",
  fontWeight: "medium",
  padding: "1 2",
  borderWidth: "1px",
  borderColor: "#fbbf24",
  borderRadius: "sm",
  cursor: "pointer",
  backgroundColor: "transparent",
  color: "#fef3c7",
});

/**
 * The persistent, non-dismissible "DEMO" banner (tasks/demo-environment.md "Demo mode" indicator, edge
 * cases 2 + 4). Rendered off the SAME `isDemo` bootstrap flag the three backend isolation seams consult,
 * so the indicator can NEVER disagree with actual behaviour. It permanently surfaces "sandbox — nothing
 * sent" (edge case 4: a rep clicking "send SMS" is reassured no real message left the platform) and hosts
 * a confirm-gated reset (edge case 2: a destructive reset never fires on a single click). On a real venue
 * the flag is false and this renders nothing.
 */
export function DemoBanner() {
  const { venueId } = useVenue();
  const { t } = useTranslation();
  const context = useDemoContext(venueId);
  const reset = useDemoReset(venueId);
  const [confirming, setConfirming] = useState(false);

  if (!context.data?.isDemo) return null;

  return (
    <section
      aria-label={t("demo.tag")}
      aria-live="polite"
      className={banner}
      data-testid="demo-banner"
    >
      <span className={tag}>{t("demo.tag")}</span>
      <span className={note}>{t("demo.sandboxNote")}</span>
      {reset.data?.outcome === "SKIPPED_ACTIVE_SESSION" ? (
        <span>{t("demo.reset.skipped")}</span>
      ) : reset.isSuccess ? (
        <span>{t("demo.reset.done")}</span>
      ) : reset.isError ? (
        <span>{t("demo.reset.error")}</span>
      ) : null}
      {confirming ? (
        <>
          <span>{t("demo.reset.confirmPrompt")}</span>
          <button
            type="button"
            className={btn}
            disabled={reset.isPending}
            onClick={() => {
              setConfirming(false);
              reset.mutate();
            }}
          >
            {t("demo.reset.confirm")}
          </button>
          <button type="button" className={btn} onClick={() => setConfirming(false)}>
            {t("demo.reset.cancel")}
          </button>
        </>
      ) : (
        <button type="button" className={btn} onClick={() => setConfirming(true)}>
          {t("demo.reset.action")}
        </button>
      )}
    </section>
  );
}
